import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/profiles/${userId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        data: error.response?.data,
        status: error.response?.status
      });
    }
  }
);

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await api.patch('/profiles/me/', profileData);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        data: error.response?.data,
        status: error.response?.status
      });
    }
  }
);

export const fetchSuggestions = createAsyncThunk(
  'profile/fetchSuggestions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/profiles/suggestions');
      return response.data;
    } catch (error) {
      return rejectWithValue({
        data: error.response?.data,
        status: error.response?.status
      });
    }
  }
);

export const uploadProfilePhoto = createAsyncThunk(
  'profile/uploadProfilePhoto',
  async ({ profileId, photoFile }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);

      const response = await api.post(`/profiles/${profileId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({
        data: error.response?.data,
        status: error.response?.status
      });
    }
  }
);

export const uploadCoverPhoto = createAsyncThunk(
  'profile/uploadCoverPhoto',
  async ({ profileId, coverFile }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('cover', coverFile);

      const response = await api.post(`/profiles/${profileId}/cover`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({
        data: error.response?.data,
        status: error.response?.status
      });
    }
  }
);

const initialState = {
  currentProfile: null,
  suggestions: [],
  loading: false,
  error: null,
  errorStatus: null,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: (state) => {
      state.error = null;
      state.errorStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorStatus = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProfile = action.payload.data || action.payload;
        state.error = null;
        state.errorStatus = null;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.data;
        state.errorStatus = action.payload.status;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.currentProfile = action.payload.data;
      })
      .addCase(fetchSuggestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestions = Array.isArray(action.payload) ? action.payload : (Array.isArray(action.payload?.data) ? action.payload.data : []);
      })
      .addCase(fetchSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentProfile) {
          state.currentProfile.photo_url = action.payload.data.photo_url;
        }
      })
      .addCase(uploadProfilePhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadCoverPhoto.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadCoverPhoto.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentProfile) {
          state.currentProfile.cover_photo = action.payload.data.cover_photo;
        }
      })
      .addCase(uploadCoverPhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export const { clearProfileError } = profileSlice.actions;
export default profileSlice.reducer;
